import { useState, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppShell from './layouts/AppShell';
import DashboardPage from './pages/DashboardPage';
import BerkasPage from './pages/BerkasPage';
import BerkasUploadPage from './pages/BerkasUploadPage';
import BerkasDetailPage from './pages/BerkasDetailPage';
import AnalisaPage from './pages/AnalisaPage';
import AuditPage from './pages/AuditPage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';
import AIPlaygroundPage from './pages/AIPlaygroundPage';
import TanyaAIPage from './pages/TanyaAIPage';
import JobsPage from './pages/JobsPage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import {
  AuthContext,
  AuthUser,
  clearStoredUser,
  getStoredUser,
  setStoredUser,
} from './shared/auth';
import { canAccessPath } from './shared/roles';
import { TaskRunnerProvider } from './shared/task-runner';
import { JobsProvider } from './shared/jobs';

function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const user = getStoredUser();
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return children;
}

/** Redirect away from a menu the current role may not access. */
function RequireRole({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const user = getStoredUser();
  if (!canAccessPath(user?.allowed_tabs, location.pathname)) {
    return <Navigate to="/analisa" replace />;
  }
  return children;
}

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'netra.current_user') {
        setUser(getStoredUser());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback((u: AuthUser) => {
    setStoredUser(u);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    clearStoredUser();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <TaskRunnerProvider>
      <JobsProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/analisa" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="berkas" element={<BerkasPage />} />
          <Route path="berkas/upload" element={<BerkasUploadPage />} />
          <Route path="berkas/:id" element={<BerkasDetailPage />} />
          <Route path="analisa" element={<AnalisaPage />} />
          <Route path="audit" element={<RequireRole><AuditPage /></RequireRole>} />
          <Route path="admin" element={<RequireRole><AdminPage /></RequireRole>} />
          <Route path="privacy" element={<RequireRole><PrivacyPage /></RequireRole>} />
          <Route path="ai-playground" element={<AIPlaygroundPage />} />
          <Route path="tanya" element={<TanyaAIPage />} />
          <Route path="jobs" element={<JobsPage />} />
        </Route>
      </Routes>
      </JobsProvider>
      </TaskRunnerProvider>
    </AuthContext.Provider>
  );
}
